
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAnalytics } from '@/hooks/useAnalytics';

/**
 * Component that tracks page views automatically using GA4 only
 */
export const PageViewTracker: React.FC = () => {
  const location = useLocation();
  const { trackPageView, initializeAnalytics, trackEvent } = useAnalytics();
  
  useEffect(() => {
    // Initialize analytics when component mounts
    console.log("PageViewTracker: Initializing GA4 analytics");
    initializeAnalytics();
  }, [initializeAnalytics]);
  
  useEffect(() => {
    try {
      // Get a friendly page title based on the path
      const getPageTitle = (path: string) => {
        const normalizedPath = path.endsWith('/') && path !== '/' ? path.slice(0, -1) : path;
        const pathParts = normalizedPath.split('/').filter(Boolean);
        const lastPart = pathParts.length ? pathParts[pathParts.length - 1] : 'home';
        
        return lastPart
          .split('-')
          .map(part => part.charAt(0).toUpperCase() + part.slice(1))
          .join(' ');
      };
      
      // Simplified referrer handling
      const getReferrer = () => {
        const urlParams = new URLSearchParams(window.location.search);
        const sourceParam = urlParams.get('source');
        
        if (sourceParam) {
          return sourceParam;
        }
        
        const previousPage = sessionStorage.getItem('lastVisitedPage');
        if (previousPage && previousPage !== location.pathname) {
          return previousPage;
        }
        
        return document.referrer || 'direct';
      };
      
      // Save current page for next visit
      sessionStorage.setItem('lastVisitedPage', location.pathname);
      
      const pageTitle = getPageTitle(location.pathname);
      const pagePath = location.pathname;
      const referrer = getReferrer();
      
      console.log(`Tracking GA4 page view: ${pageTitle} (${pagePath}) from ${referrer}`);
      
      // Track the page view
      trackPageView(pageTitle, pagePath, referrer);
      
      // Also track as a navigation event
      trackEvent('navigation', {
        destination: pagePath,
        title: pageTitle,
        referrer: referrer
      });
    } catch (error) {
      console.error("Error tracking page view:", error);
    }
  }, [location, trackPageView, trackEvent]);
  
  return null;
};
